import { useState, useEffect, useContext } from 'react';
import { Link } from 'react-router-dom';
import { AuthContext } from '../context/authContext';
import { optionsPartido } from '../datos/options.js';

//const API_URL = import.meta.env.VITE_API_URL;

const EntidadRequestsListPage = () => {
    const { token, role, user, userMunicipio } = useContext(AuthContext);
    const [requests, setRequests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [filter, setFilter] = useState('all'); // all, pendiente, validado, aprobado, rechazado
    const [partidoFilter, setPartidoFilter] = useState('all'); // filtro por partido para editor_provincial

    useEffect(() => {
        fetchRequests();
    }, [token]);

    const fetchRequests = async () => {
        try {
            const response = await fetch(`/api/entidad-requests/requests`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (!response.ok) {
                throw new Error('Error al cargar las solicitudes');
            }

            const data = await response.json();
            setRequests(data);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const getStatusBadge = (status) => {
        const styles = {
            pendiente: 'bg-yellow-100 text-yellow-800',
            validado: 'bg-blue-100 text-blue-800',
            aprobado: 'bg-green-100 text-green-800',
            rechazado: 'bg-red-100 text-red-800',
            completado: 'bg-purple-100 text-purple-800',
            baja: 'bg-gray-100 text-gray-600'
        };

        const labels = {
            pendiente: 'Pendiente',
            validado: 'Validado',
            aprobado: 'Aprobado',
            rechazado: 'Rechazado',
            completado: 'Completado',
            baja: 'Baja'
        };

        return (
            <span className={`px-2 py-1 rounded-full text-xs font-semibold flex items-center gap-1 ${styles[status] || 'bg-gray-100'}`}>
                {status === 'baja' && (
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                )}
                {labels[status] || status}
            </span>
        );
    };

    // Filtrar por estado, municipio y partido
    const filteredRequests = requests.filter(req => {
        // Filtro por estado
        if (filter !== 'all' && req.status !== filter) return false;

        // Filtro por partido para editor_provincial
        if (partidoFilter !== 'all' && req.codigo_partido !== partidoFilter) {
            return false;
        }

        // Filtro por municipio para usuarios municipales
        if (role === 'editor_municipio' && userMunicipio) {
            return req.codigo_partido === userMunicipio;
        }

        return true;
    });

    if (loading) {
        return (
            <div className="container mx-auto p-6">
                <div className="text-center py-10">Cargando solicitudes...</div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="container mx-auto p-6">
                <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
                    {error}
                </div>
            </div>
        );
    }

    return (
        <div className="container mx-auto p-6">
            <h1 className="text-2xl font-bold mb-6 text-gray-800 dark:text-white">
                Solicitudes de Entidades Deportivas
            </h1>

            {/* Filters */}
            <div className="mb-6 flex gap-2 flex-wrap items-center">
                {['all', 'pendiente', 'validado', 'aprobado', 'rechazado', 'completado', 'baja'].map((f) => (
                    <button
                        key={f}
                        onClick={() => setFilter(f)}
                        className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                            filter === f
                                ? 'bg-blue-500 text-white'
                                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                        }`}
                    >
                        {f === 'all' && 'Todas'}
                        {f === 'pendiente' && 'Pendientes'}
                        {f === 'validado' && 'Validadas'}
                        {f === 'aprobado' && 'Aprobadas'}
                        {f === 'rechazado' && 'Rechazadas'}
                        {f === 'completado' && 'Completadas'}
                        {f === 'baja' && 'Dadas de Baja'}
                    </button>
                ))}

                {/* Filtro por partido solo para editor_provincial */}
                {role === 'editor_provincial' && (
                    <select
                        value={partidoFilter}
                        onChange={(e) => setPartidoFilter(e.target.value)}
                        className="px-4 py-2 rounded-md text-sm font-medium border border-gray-300 bg-white text-gray-700 hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                        <option value="all">Todos los partidos</option>
                        {[...new Set(requests.map(r => r.codigo_partido).filter(Boolean))].sort().map((codigo) => {
                            const partido = optionsPartido.find(p => p.value === codigo);
                            return (
                                <option key={codigo} value={codigo}>
                                    {partido ? partido.label : codigo}
                                </option>
                            );
                        })}
                    </select>
                )}
            </div>

            {/* Requests Table */}
            {filteredRequests.length === 0 ? (
                <div className="text-center py-10 text-gray-500">
                    No hay solicitudes para mostrar
                </div>
            ) : (
                <div className="overflow-x-auto">
                    <table className="min-w-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg">
                        <thead className="bg-gray-50 dark:bg-gray-700">
                            <tr>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                    ID
                                </th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                    Institución
                                </th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                    Partido
                                </th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                    Email Contacto
                                </th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                    Fecha
                                </th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                    Estado
                                </th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                    Asignado a
                                </th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                    Acciones
                                </th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                            {filteredRequests.map((request) => (
                                <tr key={request.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                                    <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">
                                        {request.id}
                                    </td>
                                    <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">
                                        {request.nombre_institucion}
                                    </td>
                                    <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">
                                        {request.nombre_partido || request.codigo_partido}
                                    </td>
                                    <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">
                                        {request.email_contacto}
                                    </td>
                                    <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">
                                        {new Date(request.created_at).toLocaleDateString()}
                                    </td>
                                    <td className="px-4 py-3 text-sm">
                                        {getStatusBadge(request.status)}
                                    </td>
                                    <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">
                                        {request.validated_by_municipio ? 'Municipio' : 
                                         request.validated_by_provincial ? 'Provincia' : 
                                         request.assigned_to_municipal ? 'Municipio' :
                                         request.assigned_to_provincial ? 'Provincia' : '-'}
                                    </td>
                                    <td className="px-4 py-3 text-sm">
                                        <Link
                                            to={`/admin/solicitudes-entidades/${request.id}`}
                                            className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 font-medium"
                                        >
                                            Ver detalle
                                        </Link>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

export default EntidadRequestsListPage;
